import { useEffect, useMemo, useState } from "react";
import { Loader2, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { createDeal } from "@/api/deals-api";
import { inboxApi } from "@/api/inbox-api";
import { getErrorMessage } from "@/lib/api-error";
import { IInboundEmail } from "@/types/data-models";
import {
  AutoCreateCandidate,
  candidatesFrom,
} from "./auto-create-candidates";

export { candidatesFrom } from "./auto-create-candidates";

type Outcome = {
  emailId: string;
  companyName: string;
  ok: boolean;
  message?: string;
};

export function AutoCreateDealsDialog({
  open,
  onOpenChange,
  emails,
  workspaceId,
  userId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  emails: IInboundEmail[];
  workspaceId: string;
  userId: string;
  onCreated: () => void;
}) {
  const candidates = useMemo(() => candidatesFrom(emails), [emails]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isRunning, setIsRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);

  // Everything is checked when the dialog opens: the point of the feature is
  // "I forwarded a pile of these, make the deals" — opting in one by one is the
  // work we are trying to remove.
  useEffect(() => {
    if (open) {
      setSelected(new Set(candidates.map((c) => c.email._id)));
      setOutcomes([]);
      setDone(0);
      setIsRunning(false);
    }
  }, [open, candidates]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allSelected = selected.size === candidates.length && candidates.length > 0;

  const createOne = async (candidate: AutoCreateCandidate) => {
    const { email, companyName, website } = candidate;

    const companyResponse = await createDeal.createCompanyPitchbox({
      userId,
      workspaceId,
      companyName,
      companyWebsite: website ?? "",
    });

    // Any response without a usable company id is a failure whatever the status
    // says — reading `.result._id` blind surfaces a TypeError instead of the
    // backend's message.
    const companyId = companyResponse?.result?._id;
    if (!companyId) {
      throw new Error(
        companyResponse?.message || "Could not create the company"
      );
    }

    // Bind the attachments BEFORE the application exists. The report sequence is
    // seeded one-shot from the company's files at creation time, so anything
    // bound afterwards misses it — that is how deals end up with reports that
    // silently lack their pitch deck. Non-fatal on its own: a deal without its
    // deck still beats no deal.
    // `company pitchdeck`, not the "Pitch Deck" label: that is the value
    // `VALID_FILE_TYPES` allows and the one every reader looks for
    // (`hasPitchDeck` in fields.ts, `getPitchdeckFile` in table-view). The
    // label went out unnoticed because the backend writes this with
    // `findByIdAndUpdate`, which does not run enum validators — so the write
    // succeeded and the deck quietly stopped counting as one.
    const fileTypes: Record<string, string> = {};
    for (const attachment of email.attachments ?? []) {
      if (attachment.file_upload_id) {
        fileTypes[attachment.file_upload_id] = "company pitchdeck";
      }
    }
    if (Object.keys(fileTypes).length > 0) {
      try {
        await inboxApi.attachFilesToCompany(email._id, companyId, fileTypes);
      } catch (error) {
        console.error("Failed to bind email attachments", email._id, error);
      }
    }

    await createDeal.createOpportunityApplication(
      companyId,
      workspaceId,
      undefined,
      "Email"
    );

    // file_types omitted deliberately: the attachments were typed and bound
    // above, and passing them again re-triggers ingestion on top of the
    // create-path sequence.
    await inboxApi.linkToDeal(
      email._id,
      companyId,
      companyId,
      undefined,
      workspaceId
    );
  };

  const run = async () => {
    const queue = candidates.filter((c) => selected.has(c.email._id));
    if (queue.length === 0) return;

    setIsRunning(true);
    setOutcomes([]);
    setDone(0);

    const results: Outcome[] = [];
    // Sequential, not Promise.all: each deal is several writes and the backend
    // resolves the application by (company, workspace). Firing a dozen of these
    // at once buys nothing and makes a partial failure much harder to read.
    for (const candidate of queue) {
      try {
        await createOne(candidate);
        results.push({
          emailId: candidate.email._id,
          companyName: candidate.companyName,
          ok: true,
        });
      } catch (error) {
        results.push({
          emailId: candidate.email._id,
          companyName: candidate.companyName,
          ok: false,
          message: getErrorMessage(error, "Failed to create the deal"),
        });
      }
      setDone((n) => n + 1);
      setOutcomes([...results]);
    }

    setIsRunning(false);

    const created = results.filter((r) => r.ok).length;
    const failed = results.length - created;

    if (created > 0) onCreated();

    if (failed === 0) {
      toast.success(
        created === 1 ? "1 deal created" : `${created} deals created`
      );
      onOpenChange(false);
    } else {
      toast.warning(
        `${created} created, ${failed} failed. The failures are listed in the dialog.`
      );
    }
  };

  const outcomeFor = (id: string) => outcomes.find((o) => o.emailId === id);

  return (
    <Dialog open={open} onOpenChange={isRunning ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create deals from these emails</DialogTitle>
          <DialogDescription>
            {candidates.length === 0
              ? "Nothing is ready yet. Emails appear here once the extraction finishes and finds a company name."
              : "One deal per email, with the attachments and the email linked to it."}
          </DialogDescription>
        </DialogHeader>

        {candidates.length > 0 && (
          <>
            <div className="flex items-center justify-between border-b pb-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={allSelected}
                  disabled={isRunning}
                  onCheckedChange={(checked) =>
                    setSelected(
                      checked
                        ? new Set(candidates.map((c) => c.email._id))
                        : new Set()
                    )
                  }
                />
                Select all
              </label>
              <span className="text-xs text-muted-foreground">
                {selected.size} of {candidates.length}
              </span>
            </div>

            <ScrollArea className="max-h-[320px] pr-3">
              <div className="space-y-1">
                {candidates.map((candidate) => {
                  const outcome = outcomeFor(candidate.email._id);
                  return (
                    <div
                      key={candidate.email._id}
                      className="flex items-start gap-2.5 rounded-md px-1 py-2"
                    >
                      <Checkbox
                        className="mt-0.5"
                        checked={selected.has(candidate.email._id)}
                        disabled={isRunning || !!outcome?.ok}
                        onCheckedChange={() => toggle(candidate.email._id)}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {candidate.companyName}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {candidate.email.from_name || candidate.email.from_email}
                          {candidate.email.subject
                            ? ` — ${candidate.email.subject}`
                            : ""}
                        </p>
                        {outcome && !outcome.ok && (
                          <p className="mt-0.5 text-xs text-destructive">
                            {outcome.message}
                          </p>
                        )}
                      </div>
                      {outcome?.ok && (
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                      )}
                      {outcome && !outcome.ok && (
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            {isRunning && (
              <Progress value={(done / Math.max(selected.size, 1)) * 100} />
            )}
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isRunning}
          >
            {outcomes.length > 0 && !isRunning ? "Close" : "Cancel"}
          </Button>
          {candidates.length > 0 && (
            <Button onClick={run} disabled={isRunning || selected.size === 0}>
              {isRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isRunning
                ? `Creating ${done}/${selected.size}...`
                : `Create ${selected.size} deal${selected.size === 1 ? "" : "s"}`}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
